import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getMemberAccessToken } from '../auth/supabase';
import { downloadCsv } from './exportUtils';
import './admin.css';
import './v12-admin.css';

function when(value) {
  const date = new Date(value || '');
  return Number.isNaN(date.getTime()) ? String(value || '') : date.toLocaleString();
}

function safeLabel(value) {
  return String(value || '—').replaceAll('_', ' ').replace(/\b\w/g, letter => letter.toUpperCase());
}

function providerEventLabel(event) {
  const normalized = String(event?.normalized_event || '');
  const labels = {
    bluetooth_unlock: 'Bluetooth Unlock',
    bluetooth_set_time: 'Bluetooth Time Sync',
    wrong_pin: 'Wrong PIN Attempt',
    pin_max_length_exceeded: 'PIN Length Rejected',
    master_pin_unlock: 'Master PIN Unlock',
    generated_pin_unlock: 'Generated PIN Unlock',
    one_time_pin_unlock: 'One-Time PIN Unlock',
    permanent_pin_first_use: 'Permanent PIN Unlock',
    duration_pin_first_use: 'Duration PIN Unlock',
    weekly_pin_first_use: 'Weekly PIN Unlock',
    monthly_pin_first_use: 'Monthly PIN Unlock',
    weekday_pin_first_use: 'Weekday PIN Unlock',
    weekend_pin_first_use: 'Weekend PIN Unlock',
    bluetooth_lock: 'Bluetooth Lock',
    keycard_unlock: 'Keycard Unlock',
    auto_relock_after_open_close: 'Auto-Lock After Close',
    auto_relock_without_open: 'Auto-Lock',
    keypad_button_lock: 'Keypad / Button Lock',
    button_unlock: 'Button Unlock',
    key_thumbturn_lock: 'Key / Thumbturn Lock',
    key_thumbturn_unlock: 'Key / Thumbturn Unlock',
    attempted_break_in: 'Attempted Break-In',
    fingerprint_unlock: 'Fingerprint Unlock',
    invalid_keycard_attempt: 'Invalid Keycard Attempt',
    bridge_online: 'Bridge Online',
    bridge_offline: 'Bridge Offline',
    bridge_connection: 'Bridge Connection',
    bridge_battery_result: 'Bridge Battery Reading',
    bridge_device_status_result: 'Bridge Device Status',
    bridge_job_complete: 'Bridge Job Complete',
  };
  return labels[normalized] || safeLabel(normalized || ('provider event ' + String(event?.provider_event_type || '')));
}

function providerEventDetail(event) {
  if (event?.battery_level != null && Number.isFinite(Number(event.battery_level))) return 'Battery ' + Number(event.battery_level) + '%';
  if (typeof event?.lock_open === 'boolean' || typeof event?.door_open === 'boolean') {
    const mechanism = typeof event?.lock_open === 'boolean' ? 'Mechanism ' + (event.lock_open ? 'open' : 'not open') : null;
    const door = typeof event?.door_open === 'boolean' ? 'Door ' + (event.door_open ? 'open' : 'closed') : null;
    return [mechanism, door].filter(Boolean).join(' · ');
  }
  if (typeof event?.bridge_online === 'boolean') return event.bridge_online ? 'Connected to igloohome' : 'Disconnected from igloohome';
  if (event?.job_status != null) return 'Job status ' + event.job_status;
  if (event?.log_type != null) return 'Hardware log ' + event.log_type;
  return 'Signed igloohome hardware event';
}

function scopeSuffix(result) {
  const scope = String(result?.grantedScope || '').trim();
  return scope ? ` · granted: ${scope}` : '';
}

function deviceVisibilitySummary(result) {
  if (!result) return 'Not checked';
  if (!result.ok) {
    return `Check failed${result?.status ? ` · HTTP ${result.status}` : ''}${result?.reason ? ` · ${safeLabel(result.reason)}` : ''}`;
  }

  const configured = String(result?.configuredDeviceId || '').trim();
  if (result.visible) {
    const match = result?.matchedDevice || {};
    const deviceId = String(match.deviceId || match.bluetoothId || configured || '').trim();
    const internalId = String(match.id || '').trim();
    const name = String(match.deviceName || '').trim();
    return `Visible${deviceId ? ` · Bluetooth ID: ${deviceId}` : ''}${internalId && internalId !== deviceId ? ` · API id: ${internalId}` : ''}${name ? ` · ${name}` : ''}${scopeSuffix(result)}`;
  }

  const returned = Array.isArray(result?.devices)
    ? result.devices
      .map(device => String(device?.deviceId || device?.bluetoothId || device?.id || '').trim())
      .filter(Boolean)
      .join(', ')
    : '';

  return `Not found${configured ? ` · configured: ${configured}` : ''}${returned ? ` · API returned: ${returned}` : ' · API returned no devices'}${scopeSuffix(result)}`;
}

function bridgeTelemetrySummary(result) {
  const telemetry = result?.bridgeTelemetry;
  if (!telemetry) return 'Not checked';
  if (!telemetry.bridgeVisible) return 'Bridge not identified in the API device list';
  const bridge = telemetry.bridge || {};
  const name = String(bridge.deviceName || 'Bridge').trim();
  const id = String(bridge.deviceId || bridge.bluetoothId || bridge.id || '').trim();
  return `Visible · ${name}${id ? ` · ${id}` : ''}`;
}

function lockTelemetrySummary(result) {
  const lock = result?.bridgeTelemetry?.lock;
  if (!lock) return 'Lock telemetry not returned';
  const battery = Number.isFinite(Number(lock.batteryLevel)) ? `${Number(lock.batteryLevel)}%` : 'Not reported';
  const synced = lock.lastSync ? when(lock.lastSync) : 'Not reported';
  return `Battery: ${battery} · Last sync: ${synced}`;
}

function ekeyProbeSummary(result) {
  if (!result) return 'Not run';
  const probe = result?.ekeyProbe || {};
  const status = probe?.status ? `HTTP ${probe.status}` : 'No HTTP status';
  if (probe.ok) return `${status} · GuestKey created · physical unlock not attempted`;
  return `${status} · Failed${probe?.reason ? ` · ${probe.reason}` : ''}`;
}

function groupedStatus(rows = []) {
  const ble = rows.find(row => row.event_type === 'unlock_result' && row.method === 'bluetooth');
  const pin = rows.find(row => row.event_type === 'backup_pin_issued');
  if (ble?.result === 'failure' && pin?.result === 'success') return 'BLE Failed · Backup PIN Issued';
  if (ble?.result === 'success') return 'Bluetooth Success';
  if (pin?.result === 'success') return 'Backup PIN Issued';
  if (ble?.result === 'failure') return 'Bluetooth Failed';
  return safeLabel((pin || ble || rows[0] || {}).result);
}

async function loadDoorEvents() {
  const token = await getMemberAccessToken();
  if (!token) throw new Error('Member sign in required.');
  const response = await fetch('/api/door-access', {
    method: 'POST',
    cache: 'no-store',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'admin_events', limit: 200 }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload?.error || 'Unable to load Door Access audit events.');
  return payload;
}

async function callIglooDiagnostic(action = 'status') {
  const token = await getMemberAccessToken();
  if (!token) throw new Error('Member sign in required.');
  const response = await fetch('/api/igloo-provider-status', {
    method: 'POST',
    cache: 'no-store',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ action }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload?.error || 'Unable to verify igloohome provider access.');
  return payload;
}

export default function DoorAccessAdmin({ profile, onBack }) {
  const [events, setEvents] = useState([]);
  const [providerEvents, setProviderEvents] = useState([]);
  const [device, setDevice] = useState(null);
  const [query, setQuery] = useState('');
  const [resultFilter, setResultFilter] = useState('all');
  const [methodFilter, setMethodFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [providerCheck, setProviderCheck] = useState(null);
  const [providerSnapshotCheckedAt, setProviderSnapshotCheckedAt] = useState(null);
  const [checkingProvider, setCheckingProvider] = useState(false);
  const [ekeyProbe, setEkeyProbe] = useState(null);
  const [probingEkey, setProbingEkey] = useState(false);
  const [bridgeActivityProbe, setBridgeActivityProbe] = useState(null);
  const [probingBridgeActivity, setProbingBridgeActivity] = useState(false);
  const [bridgeTelemetryProbe, setBridgeTelemetryProbe] = useState(null);
  const [probingBridgeTelemetry, setProbingBridgeTelemetry] = useState(false);

  const load = useCallback(async () => {
    if (!profile?.is_admin) return;
    setError('');
    setStatus('loading');
    try {
      const payload = await loadDoorEvents();
      setEvents(Array.isArray(payload?.events) ? payload.events : []);
      setProviderEvents(Array.isArray(payload?.providerEvents) ? payload.providerEvents : []);
      setDevice(payload?.device || null);
      setStatus('ready');
    } catch (loadError) {
      setError(loadError?.message || 'Unable to load Door Access audit events.');
      setStatus('error');
    }
  }, [profile]);

  useEffect(() => { load(); }, [load]);

  const runProviderCheck = async () => {
    setCheckingProvider(true);
    setError('');
    try {
      setProviderCheck(await callIglooDiagnostic('status'));
      setProviderSnapshotCheckedAt(new Date().toISOString());
    } catch (checkError) {
      setProviderCheck(null);
      setError(checkError?.message || 'Unable to verify igloohome provider access.');
    } finally {
      setCheckingProvider(false);
    }
  };

  const runEkeyProbe = async () => {
    setProbingEkey(true);
    setError('');
    try {
      setEkeyProbe(await callIglooDiagnostic('probe_ekey'));
    } catch (probeError) {
      setEkeyProbe(null);
      setError(probeError?.message || 'Unable to probe the igloohome eKey endpoint.');
    } finally {
      setProbingEkey(false);
    }
  };

  const runBridgeActivityProbe = async () => {
    setProbingBridgeActivity(true);
    setError('');
    try {
      setBridgeActivityProbe(await callIglooDiagnostic('probe_bridge_activity'));
    } catch (probeError) {
      setBridgeActivityProbe(null);
      setError(probeError?.message || 'Unable to retrieve Bridge activity.');
    } finally {
      setProbingBridgeActivity(false);
    }
  };

  const runBridgeTelemetryProbe = async () => {
    setProbingBridgeTelemetry(true);
    setError('');
    try {
      const [result, provider] = await Promise.all([
        callIglooDiagnostic('probe_bridge_telemetry'),
        callIglooDiagnostic('status'),
      ]);
      setBridgeTelemetryProbe(result);
      setProviderCheck(provider);
      setProviderSnapshotCheckedAt(new Date().toISOString());
      await load();

      // Bridge job results arrive asynchronously through the signed webhook.
      // Refresh the admin timeline a few times so the verified measurement
      // appears without making the HTTP request itself pretend to be live state.
      [4500, 9000, 14000].forEach(delay => {
        window.setTimeout(() => { load(); }, delay);
      });
    } catch (probeError) {
      setBridgeTelemetryProbe(null);
      setError(probeError?.message || 'Unable to refresh Bridge telemetry.');
    } finally {
      setProbingBridgeTelemetry(false);
    }
  };

  const inDateRange = useCallback(value => {
    const timestamp = Date.parse(value || '');
    if (!Number.isFinite(timestamp)) return !startDate && !endDate;
    if (startDate) {
      const start = Date.parse(`${startDate}T00:00:00`);
      if (Number.isFinite(start) && timestamp < start) return false;
    }
    if (endDate) {
      const end = Date.parse(`${endDate}T23:59:59.999`);
      if (Number.isFinite(end) && timestamp > end) return false;
    }
    return true;
  }, [startDate, endDate]);

  const filtered = useMemo(() => {
    const search = query.trim().toLowerCase();
    return events.filter(event => {
      const matchesResult = resultFilter === 'all' || String(event.result || '') === resultFilter;
      const matchesMethod = methodFilter === 'all' || String(event.method || '') === methodFilter;
      if (!matchesResult || !matchesMethod || !inDateRange(event.created_at)) return false;
      if (!search) return true;
      return [event.member_name, event.member_email, event.request_id, event.event_type, event.method, event.error_code]
        .some(value => String(value || '').toLowerCase().includes(search));
    });
  }, [events, query, resultFilter, methodFilter, inDateRange]);

  const filteredProviderEvents = useMemo(() => {
    const search = query.trim().toLowerCase();
    return providerEvents
      .filter(event => inDateRange(event.provider_event_timestamp || event.received_at))
      .filter(event => !search || [
        providerEventLabel(event),
        event.normalized_event,
        event.provider_event_type,
        event.request_id,
        event?.correlation?.memberName,
        event?.correlation?.memberEmail,
      ].some(value => String(value || '').toLowerCase().includes(search)))
      .sort((a, b) => Date.parse(b.provider_event_timestamp || b.received_at || '') - Date.parse(a.provider_event_timestamp || a.received_at || ''));
  }, [providerEvents, query, inDateRange]);

  const grouped = useMemo(() => {
    const requests = new Map();
    filtered.forEach(event => {
      const key = event.request_id || event.id;
      const list = requests.get(key) || [];
      list.push(event);
      requests.set(key, list);
    });
    return [...requests.entries()].map(([requestId, rows]) => ({ requestId, rows })).sort((a, b) => Date.parse(b.rows[0]?.created_at || '') - Date.parse(a.rows[0]?.created_at || ''));
  }, [filtered]);

  const providerSummary = useMemo(() => {
    const sorted = [...providerEvents].sort((a, b) => Date.parse(b.provider_event_timestamp || b.received_at || '') - Date.parse(a.provider_event_timestamp || a.received_at || ''));
    const bridgeConnection = sorted.find(event => typeof event.bridge_online === 'boolean') || null;
    const battery = sorted.find(event => event?.battery_level != null && Number.isFinite(Number(event.battery_level))) || null;
    const deviceStatus = sorted.find(event => typeof event.lock_open === 'boolean' || typeof event.door_open === 'boolean') || null;
    return { sorted, bridgeConnection, battery, deviceStatus };
  }, [providerEvents]);

  const telemetryRequestStatus = useMemo(() => {
    const probe = bridgeTelemetryProbe?.telemetryProbe;
    if (!probe) return null;
    const batteryJobId = String(probe?.battery?.jobId || '');
    const statusJobId = String(probe?.deviceStatus?.jobId || '');
    const batteryResponse = batteryJobId
      ? providerSummary.sorted.find(event => String(event?.job_id || '') === batteryJobId) || null
      : null;
    const statusResponse = statusJobId
      ? providerSummary.sorted.find(event => String(event?.job_id || '') === statusJobId) || null
      : null;
    return { batteryJobId, statusJobId, batteryResponse, statusResponse };
  }, [bridgeTelemetryProbe, providerSummary]);

  const providerBatterySnapshot = useMemo(() => {
    const lock = providerCheck?.deviceVisibility?.bridgeTelemetry?.lock;
    const value = lock?.batteryLevel;
    return value != null && Number.isFinite(Number(value)) ? Number(value) : null;
  }, [providerCheck]);

  const exportAudit = async () => {
    const applicationRows = filtered.map(event => ({
      source: 'Line Medic app',
      timestamp: event.created_at,
      member: event.member_name || '',
      email: event.member_email || '',
      request_id: event.request_id || '',
      event: safeLabel(event.event_type),
      method: safeLabel(event.method),
      result: safeLabel(event.result),
      detail: event.error_code || '',
    }));
    const hardwareRows = filteredProviderEvents.map(event => ({
      source: 'igloohome hardware',
      timestamp: event.provider_event_timestamp || event.received_at,
      member: event?.correlation?.memberName || '',
      email: event?.correlation?.memberEmail || '',
      request_id: event.request_id || event?.correlation?.requestId || '',
      event: providerEventLabel(event),
      method: 'Provider / Bridge',
      result: event.signature_verified ? 'Signature verified' : 'Signature not verified',
      detail: providerEventDetail(event),
    }));
    const suffix = [startDate || 'all', endDate || 'all'].join('_to_');
    await downloadCsv(`line-medic-door-access-${suffix}.csv`, [...applicationRows, ...hardwareRows]);
  };

  if (!profile?.is_admin) {
    return <div className="admin-root"><main className="admin-page"><section className="admin-card admin-denied"><h1>Admin access required</h1></section></main></div>;
  }

  return (
    <div className="admin-root">
      <header className="admin-topbar">
        <div><h1>Door Access Audit</h1><p>Gear Room access history, filters, exports, and igloohome diagnostics</p></div>
        <div className="admin-topbar-actions"><button type="button" onClick={onBack || (() => window.location.assign('/admin'))}>Admin Home</button><button type="button" onClick={load}>Refresh</button></div>
      </header>
      <main className="admin-page">
        <section className="admin-card">
          <div className="admin-section-head"><div><h2>Access Audit</h2><p>Filter application and signed hardware activity, then export the current view when needed.</p></div><button type="button" onClick={() => exportAudit().catch(exportError => setError(exportError?.message || 'Unable to export Door Access audit.'))}>Export CSV</button></div>
          <div className="v12-admin-grid">
            <label>Search<input value={query} onChange={event => setQuery(event.target.value)} placeholder="Member, email, request ID, method…" /></label>
            <label>Result<select value={resultFilter} onChange={event => setResultFilter(event.target.value)}><option value="all">All results</option><option value="success">Success</option><option value="failure">Failure</option><option value="requested">Requested</option></select></label>
            <label>Method<select value={methodFilter} onChange={event => setMethodFilter(event.target.value)}><option value="all">All methods</option><option value="bluetooth">Bluetooth</option><option value="backup_pin">Backup PIN</option></select></label>
            <label>From<input type="date" value={startDate} onChange={event => setStartDate(event.target.value)} /></label>
            <label>Through<input type="date" value={endDate} onChange={event => setEndDate(event.target.value)} /></label>
          </div>
          {status === 'loading' && <div className="admin-empty">Loading Door Access audit…</div>}
          {error && <div className="v12-error">{error}</div>}
          {status === 'ready' && !grouped.length && <div className="admin-empty">No Door Access events match this filter.</div>}
          {grouped.map(group => {
            const first = group.rows[0] || {};
            return (
              <article key={group.requestId} className="admin-card" style={{ marginTop: 12, boxShadow: 'none' }}>
                <div className="admin-section-head"><div><strong>{first.member_name || first.member_email || 'Member'}</strong><p>{first.member_email || ''}</p><small>{device?.label || 'Line Medic Room'} · {when(first.created_at)} · Request {String(group.requestId).slice(0, 12)}</small></div><span className="admin-status">{groupedStatus(group.rows)}</span></div>
                <div style={{ display: 'grid', gap: 7 }}>
                  {group.rows.map(row => <div key={row.id} className="admin-table-row"><span><strong>{safeLabel(row.event_type)}</strong><small>{when(row.created_at)}</small></span><span>{safeLabel(row.method)} · {safeLabel(row.result)}{row.error_code ? ` · ${row.error_code}` : ''}</span></div>)}
                </div>
              </article>
            );
          })}
        </section>

        <section className="admin-card">
          <div className="admin-section-head"><div><h2>{device?.label || 'Line Medic Room'}</h2><p>Server-side integration status only. Secrets, GuestKeys, OTPs, and PIN values are never displayed here.</p></div><span className="admin-status">{device?.integrationReady ? 'Integration Ready' : 'Configuration Incomplete'}</span></div>
          <div className="v12-admin-grid">
            <div className="v12-admin-note"><strong>Bluetooth device</strong><br />{device?.bluetoothName || 'Not configured'}</div>
            <div className="v12-admin-note"><strong>Battery telemetry</strong><br />Provider battery is shown conservatively. Signed Bridge measurements include their provider time; device-list fallback is labeled as a snapshot when igloohome does not report measurement freshness.</div>
          </div>
          <div className="v12-admin-note" style={{ marginTop: 12 }}>
            <strong>Provider readiness</strong><br />Safely verifies the configured igloohome OAuth scopes and asks the provider which devices this API client can actually see. It does not unlock the lockbox, create a GuestKey, generate a PIN, or display secrets.
            <div style={{ marginTop: 10 }}><button type="button" onClick={runProviderCheck} disabled={checkingProvider}>{checkingProvider ? 'Checking…' : 'Check igloohome Provider'}</button></div>
            {providerCheck && <div style={{ marginTop: 10, display: 'grid', gap: 5 }}>
              <span><strong>Bluetooth/eKey:</strong> {providerCheck?.ekey?.ok ? `Ready${scopeSuffix(providerCheck.ekey)}` : `Not ready${providerCheck?.ekey?.reason ? ` · ${safeLabel(providerCheck.ekey.reason)}` : ''}`}</span>
              <span><strong>One-time AlgoPIN:</strong> {providerCheck?.algopin?.ok ? `Ready${scopeSuffix(providerCheck.algopin)}` : `Not ready${providerCheck?.algopin?.reason ? ` · ${safeLabel(providerCheck.algopin.reason)}` : ''}`}</span>
              <span><strong>Device visibility:</strong> {deviceVisibilitySummary(providerCheck?.deviceVisibility)}</span>
              <span><strong>Bridge:</strong> {bridgeTelemetrySummary(providerCheck?.deviceVisibility)}</span>
              <span><strong>Lock telemetry:</strong> {lockTelemetrySummary(providerCheck?.deviceVisibility)}</span>
              <span><strong>Default OAuth token:</strong> {providerCheck?.defaultToken?.ok ? `Ready${scopeSuffix(providerCheck.defaultToken)}` : `Not ready${providerCheck?.defaultToken?.reason ? ` · ${safeLabel(providerCheck.defaultToken.reason)}` : ''}`}</span>
            </div>}
          </div>

          <div className="v12-admin-note" style={{ marginTop: 12 }}>
            <strong>Bridge telemetry</strong><br />Requests the current SK4 battery level and device status through Station 66 Bridge. This is read-only and never locks or unlocks the SK4.
            <div style={{ marginTop: 10 }}><button type="button" onClick={runBridgeTelemetryProbe} disabled={probingBridgeTelemetry}>{probingBridgeTelemetry ? 'Refreshing…' : 'Refresh Bridge Telemetry'}</button></div>
            <div style={{ marginTop: 10, display: 'grid', gap: 5 }}>
              <span><strong>Bridge connection:</strong> {providerSummary.bridgeConnection ? (providerSummary.bridgeConnection.bridge_online ? 'Online' : 'Offline') + ' · reported ' + when(providerSummary.bridgeConnection.provider_event_timestamp || providerSummary.bridgeConnection.received_at) : 'No signed connection event received yet'}</span>
              <span><strong>Battery:</strong> {providerSummary.battery
                ? Number(providerSummary.battery.battery_level) + '% · measured ' + when(providerSummary.battery.provider_event_timestamp || providerSummary.battery.received_at)
                : providerBatterySnapshot != null
                  ? providerBatterySnapshot + '% · provider snapshot checked ' + when(providerSnapshotCheckedAt) + ' · measurement time not reported'
                  : 'No battery reading received yet'}</span>
              <span><strong>Physical state:</strong> {providerSummary.deviceStatus ? providerEventDetail(providerSummary.deviceStatus) + ' · measured ' + when(providerSummary.deviceStatus.provider_event_timestamp || providerSummary.deviceStatus.received_at) : 'No signed device-status reading received yet'}</span>
              {bridgeTelemetryProbe?.telemetryProbe && <span><strong>Latest request:</strong> {bridgeTelemetryProbe.telemetryProbe.submitted
                ? [
                    telemetryRequestStatus?.statusResponse ? 'Status response received' : 'Status response pending',
                    telemetryRequestStatus?.batteryResponse ? 'Battery response received' : 'Battery job has not returned a signed response',
                  ].join(' · ')
                : 'Not submitted' + (bridgeTelemetryProbe.telemetryProbe.reason ? ' · ' + safeLabel(bridgeTelemetryProbe.telemetryProbe.reason) : '')}</span>}
            </div>
          </div>

          <div className="v12-admin-note" style={{ marginTop: 12 }}>
            <strong>Bridge activity probe</strong><br />Asks the linked Bridge to retrieve recent lock activity. This is read-only: it does not unlock the SK4, create a GuestKey, or generate a PIN. Credential-like fields are redacted before anything is shown.
            <div style={{ marginTop: 10 }}><button type="button" onClick={runBridgeActivityProbe} disabled={probingBridgeActivity}>{probingBridgeActivity ? 'Retrieving…' : 'Get Bridge Activity (read-only)'}</button></div>
            {bridgeActivityProbe && <div style={{ marginTop: 10, display: 'grid', gap: 5 }}>
              <span><strong>Bridge activity:</strong> {bridgeActivityProbe?.activityProbe?.ok ? 'Retrieved' : `Not retrieved${bridgeActivityProbe?.activityProbe?.reason ? ` · ${safeLabel(bridgeActivityProbe.activityProbe.reason)}` : ''}`}</span>
              <span><strong>Job:</strong> {bridgeActivityProbe?.activityProbe?.completed ? 'Completed' : 'Not completed'}{bridgeActivityProbe?.activityProbe?.jobId ? ` · ${String(bridgeActivityProbe.activityProbe.jobId).slice(0, 14)}` : ''}</span>
              <span><strong>Cloud activity rows:</strong> {Number(bridgeActivityProbe?.activityProbe?.activityCount || 0)}</span>
              {bridgeActivityProbe?.activityProbe?.homeDevices && <span><strong>Home API inventory:</strong> {bridgeActivityProbe.activityProbe.homeDevices.ok ? `Visible · ${Number(bridgeActivityProbe.activityProbe.homeDevices.deviceCount || 0)} device(s)` : `Not available${bridgeActivityProbe.activityProbe.homeDevices.reason ? ` · ${safeLabel(bridgeActivityProbe.activityProbe.homeDevices.reason)}` : ''}`}</span>}
              {bridgeActivityProbe?.activityProbe?.homeDevices?.matchedLock && <span><strong>Home API lock:</strong> <code>{JSON.stringify(bridgeActivityProbe.activityProbe.homeDevices.matchedLock)}</code></span>}
              {bridgeActivityProbe?.activityProbe?.activityDeviceId && <span><strong>Activity device id accepted:</strong> {String(bridgeActivityProbe.activityProbe.activityDeviceId)}</span>}
              {Array.isArray(bridgeActivityProbe?.activityProbe?.activityAttempts) && bridgeActivityProbe.activityProbe.activityAttempts.length > 0 && <span><strong>Activity endpoint attempts:</strong> <code>{JSON.stringify(bridgeActivityProbe.activityProbe.activityAttempts)}</code></span>}
              {bridgeActivityProbe?.activityProbe?.responseShape && <span><strong>Bridge job response shape:</strong> <code>{JSON.stringify(bridgeActivityProbe.activityProbe.responseShape)}</code></span>}
              {bridgeActivityProbe?.activityProbe?.activityShape && <span><strong>Cloud activity shape:</strong> <code>{JSON.stringify(bridgeActivityProbe.activityProbe.activityShape)}</code></span>}
              {bridgeActivityProbe?.activityProbe?.sanitizedActivity != null && <span><strong>Recent sanitized device activity:</strong> <code style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{JSON.stringify(bridgeActivityProbe.activityProbe.sanitizedActivity, null, 2)}</code></span>}
              {bridgeActivityProbe?.activityProbe?.sanitizedResponse != null && <span><strong>Bridge job response:</strong> <code style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{JSON.stringify(bridgeActivityProbe.activityProbe.sanitizedResponse, null, 2)}</code></span>}
            </div>}
          </div>

          <div className="v12-admin-note" style={{ marginTop: 12 }}>
            <strong>eKey endpoint probe</strong><br />Runs the exact server-side GuestKey request used by Line Medic. If igloohome accepts it, one short-lived test GuestKey is created and immediately discarded. The credential is never displayed and no Bluetooth unlock is attempted.
            <div style={{ marginTop: 10 }}><button type="button" onClick={runEkeyProbe} disabled={probingEkey}>{probingEkey ? 'Probing…' : 'Probe eKey API (no unlock)'}</button></div>
            {ekeyProbe && <div style={{ marginTop: 10, display: 'grid', gap: 5 }}>
              <span><strong>eKey endpoint:</strong> {ekeyProbeSummary(ekeyProbe)}</span>
              <span><strong>Device visibility:</strong> {deviceVisibilitySummary(ekeyProbe?.deviceVisibility)}</span>
              <span><strong>Request:</strong> {ekeyProbe?.ekeyProbe?.request?.method || 'POST'} {ekeyProbe?.ekeyProbe?.request?.path || '—'}</span>
              <span><strong>Credential returned:</strong> {ekeyProbe?.ekeyProbe?.response?.guestKeyPresent ? 'Yes (redacted)' : 'No'}</span>
              {ekeyProbe?.ekeyProbe?.response?.shape && <span><strong>Response shape:</strong> <code>{JSON.stringify(ekeyProbe.ekeyProbe.response.shape)}</code></span>}
            </div>}
          </div>
        </section>

        <section className="admin-card">
          <div className="admin-section-head"><div><h2>Hardware Activity</h2><p>Signed igloohome events reported independently by the SK4 and Station 66 Bridge. Line Medic identity is shown only when a Bluetooth event can be matched confidently to a single successful app request.</p></div><span className="admin-status">{providerEvents.length ? providerEvents.length + ' events' : 'No events'}</span></div>
          {!filteredProviderEvents.length && <div className="admin-empty">No signed hardware activity matches the current filters.</div>}
          <div style={{ display: 'grid', gap: 8 }}>
            {filteredProviderEvents.slice(0, 60).map(event => (
              <div key={'provider-' + event.id} className="admin-table-row">
                <span>
                  <strong>{providerEventLabel(event)}</strong>
                  <small>{when(event.provider_event_timestamp || event.received_at)} · {event.signature_verified ? 'Signature verified' : 'Signature not verified'}</small>
                </span>
                <span>
                  {providerEventDetail(event)}
                  {event?.correlation?.confidence === 'high' ? <small>Matched to {event.correlation.memberName || event.correlation.memberEmail || 'Line Medic member'} · Request {String(event.correlation.requestId || '').slice(0, 12)}</small> : <small>Provider / hardware event</small>}
                </span>
              </div>
            ))}
          </div>
        </section>

      </main>
    </div>
  );
}
